import React, { useState, useMemo, useRef, useEffect } from 'react';
import styled, { css } from '@emotion/native';
import { BodyText, H3 } from '@/shared/components/StyledText';
import { sand, green, lightGreen } from '@/shared/styles/colors';
import {
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  GET_STYLIST_ID_BY_SHARE_CODE,
  GET_STYLISTS,
} from '@/shared/queries/stylistSchema';
import Button from '@/shared/components/Button';
import { ROUTES } from '@/shared/constants/routes';
import { useRouter, useLocalSearchParams } from 'expo-router';
import StylistCard from '@/components/explore/StylistCard';
import StaticInput from '@/shared/components/StaticInput';
import { FlashList } from '@shopify/flash-list';
import { TextInput } from 'react-native-paper';
import StylistProfileModal from '@/components/stylist-profile/StylistProfileModal';
import {
  QueryGetStylistIdByShareCodeArgs,
  QueryResult,
  SessionType,
} from '@/shared/types/generated';
import BookingModal from '@/components/bookConsultation/BookingModal';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import FiltersModal from '@/components/explore/filters/FiltersModal';
import useExploreStore from '@/stores/useExploreStore';
import { AgeRange } from '@/shared/types/generated';
import FilterPills from '@/components/explore/filters/FilterPills';
import Spacer from '@/shared/components/Spacer';
import { SCREEN_PADDING } from '@/components/explore/constants';
import useDimensions from '@/shared/hooks/useDimensions';
import { shuffleArray } from '@/shared/utils';
import { useClearRedirectState } from '@/shared/hooks/useClearRedirectState';
import useStore from '@/stores/useStore';
import SentryService from '@/services/sentry/Sentry.service';
import useNotificationStore from '@/stores/useNotificationStore';
import { useShallow } from 'zustand/react/shallow';

const Explore = () => {
  const isStylist = useStore((state) => state.isStylist);
  const { filters } = useExploreStore();
  const router = useRouter();
  const { clearRedirectState } = useClearRedirectState();
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [stylistProfileModalVisible, setStylistProfileModalVisible] =
    useState(false);
  const [selectedStylistId, setSelectedStylistId] = useState<number | null>(
    null,
  );
  const [bookingModalVisible, setBookingModalVisible] = useState(false);
  const [shouldShowBookingModal, setShouldShowBookingModal] = useState(false);

  // Store the shuffled order separately to maintain stability
  const shuffledOrderRef = useRef<number[]>([]);

  const { data, loading } = useQuery<QueryResult<'stylists'>>(GET_STYLISTS, {
    fetchPolicy: 'cache-and-network',
  });

  const [getStylistIdByShareCode] = useLazyQuery<
    QueryResult<'getStylistIdByShareCode'>,
    QueryGetStylistIdByShareCodeArgs
  >(GET_STYLIST_ID_BY_SHARE_CODE, {
    fetchPolicy: 'network-only',
  });

  const { parsedDeepLink } = useNotificationStore(
    useShallow((state) => ({ parsedDeepLink: state.parsedDeepLink })),
  );
  const shareCodeFromDeepLink = (() => {
    const shareCode = parsedDeepLink?.queryParams?.s;
    if (!shareCode) return undefined;
    return Array.isArray(shareCode) ? shareCode[0] : shareCode;
  })();

  const { s } = useLocalSearchParams<{ s?: string | string[] }>();
  const shareCodeFromQuery = useMemo(() => {
    if (!s) return undefined;
    return Array.isArray(s) ? s[0] : s;
  }, [s]);

  const shareCode = shareCodeFromQuery ?? shareCodeFromDeepLink;

  const hasProcessedRef = useRef(false);

  // Handle deep linking to specific stylist profile
  useEffect(() => {
    if (isStylist) {
      router.replace(ROUTES.SESSIONS.path);
      clearRedirectState();
      return;
    }

    if (!shareCode || hasProcessedRef.current) {
      return;
    }
    hasProcessedRef.current = true;

    (async () => {
      try {
        const { data } = await getStylistIdByShareCode({
          variables: { input: { shareCode } },
        });

        const stylistId = data?.getStylistIdByShareCode.stylistId;

        if (stylistId) {
          setSelectedStylistId(stylistId);
          setStylistProfileModalVisible(true);

          clearRedirectState();

          // If came from query param, clear it to avoid re-triggering without remounting
          if (shareCodeFromQuery && Platform.OS === 'web') {
            try {
              const basePath = ROUTES.EXPLORE.path;
              // Remove query string while keeping the same path
              window.history.replaceState(null, '', basePath as string);
            } catch (_) {
              // no-op if history is not available
            }
          }
        }
      } catch (error) {
        SentryService.captureError(
          'Error fetching stylist id by share code',
          error,
        );
      }
    })();
  }, [
    clearRedirectState,
    getStylistIdByShareCode,
    isStylist,
    router,
    shareCode,
    shareCodeFromQuery,
  ]);

  const { width: screenWidth } = useDimensions();
  const GAP = 11;
  const LIST_PADDING_LEFT = 19;
  const estimatedItemDimension =
    (screenWidth - GAP - LIST_PADDING_LEFT * 2) / 2; // calculate width of each card, given 2 columns and available screen space
  const estimatedItemSize = estimatedItemDimension + 54; // 54px accounts for the height of the stylist name and rate

  // First, filter the stylists based on all criteria
  const baseFilteredStylists = useMemo(() => {
    if (!data?.stylists) return [];

    // Remove trailing punctuation from the search query
    const sanitizedQuery = searchQuery
      .toLowerCase()
      .trim()
      .replace(/[.,!?;:]*$/, '');

    return data.stylists.filter((stylist) => {
      if (!stylist.user) return false;
      if (!stylist.pricing?.hourlyRate) return false;

      // Apply search query filter
      if (sanitizedQuery) {
        const fullName =
          `${stylist.user?.firstName} ${stylist.user?.lastName}`.toLowerCase();
        const firstName = stylist.user?.firstName.toLowerCase();
        const lastName = stylist.user?.lastName.toLowerCase();

        if (
          !(
            fullName.startsWith(sanitizedQuery) ||
            firstName.startsWith(sanitizedQuery) ||
            lastName.startsWith(sanitizedQuery)
          )
        ) {
          return false;
        }
      }

      // Apply price tier filter
      if (filters.priceTier.length > 0) {
        if (!filters.priceTier.includes(stylist.pricing?.tierName)) {
          return false;
        }
      }

      // Apply expertise filter
      if (filters.expertise.length > 0) {
        const hasMatchingExpertise = filters.expertise.some((expertise) =>
          stylist.expertise?.includes(expertise),
        );
        if (!hasMatchingExpertise) return false;
      }

      // Apply styling preferences filter
      if (filters.stylingPreferences.length > 0) {
        const hasMatchingPreferences = filters.stylingPreferences.some(
          (preference) => stylist.aesthetics?.includes(preference),
        );
        if (!hasMatchingPreferences) return false;
      }

      // Apply age filter
      if (filters.age.length > 0 && stylist.user?.birthDate) {
        const birthDate = new Date(stylist.user.birthDate);
        const currentDate = new Date();

        let age = currentDate.getFullYear() - birthDate.getFullYear();
        const monthDiff = currentDate.getMonth() - birthDate.getMonth();

        // Adjust age if birthday hasn't occurred yet this year
        if (
          monthDiff < 0 ||
          (monthDiff === 0 && currentDate.getDate() < birthDate.getDate())
        ) {
          age--;
        }

        let ageRange: AgeRange;

        if (age >= 65) ageRange = AgeRange.Age_65Plus;
        else if (age >= 55 && age < 65) ageRange = AgeRange.Age_55_64;
        else if (age >= 45 && age < 55) ageRange = AgeRange.Age_45_54;
        else if (age >= 35 && age < 45) ageRange = AgeRange.Age_35_44;
        else if (age >= 25 && age < 35) ageRange = AgeRange.Age_25_34;
        else if (age >= 18 && age < 25) ageRange = AgeRange.Age_18_24;
        else return false; // Under 18

        if (!filters.age.includes(ageRange)) {
          return false;
        }
      }

      // Apply gender filter
      if (filters.gender && stylist.user?.gender !== filters.gender) {
        return false;
      }

      // Apply session type filter - filter for event attendees when In Person @ Event is selected
      if (filters.sessionType === SessionType.InPersonEvent) {
        // Check if stylist is an event attendee
        if (!stylist.isEventAttendee) {
          return false;
        }
      }

      // Apply in-person availability filter
      if (filters.availableInPerson === true) {
        if (!stylist.computedInPersonAvailability) {
          return false;
        }
      }

      // Apply city filter
      if (filters.cities.length > 0) {
        const stylistCity = stylist.user?.address?.city;
        if (!stylistCity || !filters.cities.includes(stylistCity)) {
          return false;
        }
      }

      return true;
    });
  }, [data?.stylists, searchQuery, filters]);

  // Create a stable shuffled order when filtering criteria change
  const shuffledOrder = useMemo(() => {
    if (baseFilteredStylists.length === 0) return [];
    const indices = Array.from(
      { length: baseFilteredStylists.length },
      (_, i) => i,
    );
    const shuffled = shuffleArray(indices);
    shuffledOrderRef.current = shuffled;
    return shuffled;
  }, [baseFilteredStylists.length, searchQuery, filters]);

  // Finally, apply favorites filter and sorting
  const filteredStylists = useMemo(() => {
    if (baseFilteredStylists.length === 0) return [];

    const favoritesFiltered = showFavoritesOnly
      ? baseFilteredStylists.filter((stylist) => stylist.isFavorited)
      : baseFilteredStylists;

    if (showFavoritesOnly) {
      // Alphabetical order for favorited stylists (by first name)
      return favoritesFiltered.sort((a, b) => {
        const nameA = a.user?.firstName?.toLowerCase() || '';
        const nameB = b.user?.firstName?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      });
    } else {
      // Apply the stable shuffled order
      return shuffledOrder
        .map((index) => baseFilteredStylists[index])
        .filter(Boolean); // Remove any undefined items in case of length mismatch
    }
  }, [baseFilteredStylists, showFavoritesOnly, shuffledOrder]);

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const onConsultationButtonPress = () => {
    setShouldShowBookingModal(true);
    setStylistProfileModalVisible(false);
  };

  const onStylistProfileModalDismiss = () => {
    if (shouldShowBookingModal) {
      setShouldShowBookingModal(false);
      setBookingModalVisible(true);
    }
  };

  return (
    <OuterContainer>
      <HeaderContainer>
        <StyledH3>Meet Your Match</StyledH3>
        <StyledBodyText>
          Book a consult or connect instantly {'\n'}
          with an available stylist.
        </StyledBodyText>
        <ButtonContainer>
          <Button
            onPress={() => router.push(ROUTES.INSTANT_STYLING.path)}
            customLabelStyle={buttonTextStyle}
            text="get styled instantly"
            customStyle={{
              width: 232,
              height: 48,
            }}
          />
        </ButtonContainer>
        <SearchContainer>
          <StaticInput
            labelText="Search by name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.nativeEvent.text)}
            customStyles={searchStyles}
            placeholderText="Search by name"
            left={
              <TextInput.Icon
                style={{ marginRight: 0 }}
                icon={require('@/assets/images/Search.png')}
                size={18}
              />
            }
            right={
              searchQuery ? (
                <TextInput.Icon
                  style={{ marginRight: 0 }}
                  icon={() => (
                    <ClearIcon source={require('@/assets/images/Clear.png')} />
                  )}
                  size={18}
                  onPress={handleClearSearch}
                />
              ) : null
            }
          />
          <IconButton onPress={() => setShowFavoritesOnly(!showFavoritesOnly)}>
            <IconImage
              style={showFavoritesOnly ? activeHeartStyle : heartStyle}
              source={
                showFavoritesOnly
                  ? require('@/assets/images/FavoritesSelected.png')
                  : require('@/assets/images/FavoriteSmall.png')
              }
            />
          </IconButton>
          <IconButton onPress={() => setIsFilterModalVisible(true)}>
            <IconImage source={require('@/assets/images/FilterIcon.png')} />
          </IconButton>
        </SearchContainer>
        <Spacer height={16} />
        <FilterPills />
      </HeaderContainer>
      {loading ? (
        <ActivityIndicator color={green} />
      ) : (
        <ListContainer>
          {filteredStylists.length === 0 ? (
            <NoResultsContainer>
              {showFavoritesOnly ? (
                <>
                  <NoResultsTitle>
                    Keep Your {'\n'}
                    Faves Close
                  </NoResultsTitle>
                  <NoResultsText>
                    Tap the heart icon to save stylists, {'\n'}
                    so they&apos;re always easy to find when {'\n'}
                    you need them.
                  </NoResultsText>
                </>
              ) : (
                <>
                  <NoResultsTitle>
                    Hmm, {'\n'}
                    No Matches Yet!
                  </NoResultsTitle>
                  <NoResultsText>
                    Try adjusting your filters, {'\n'}
                    clearing your search, or checking the {'\n'}
                    spelling and searching again
                  </NoResultsText>
                </>
              )}
            </NoResultsContainer>
          ) : (
            <FlashList
              data={filteredStylists}
              renderItem={({ item }) => (
                <StylistCard
                  item={item}
                  estimatedItemSize={estimatedItemSize}
                  estimatedItemDimension={estimatedItemDimension}
                  onPress={() => {
                    setSelectedStylistId(item.id);
                    setStylistProfileModalVisible(true);
                  }}
                />
              )}
              numColumns={2}
              contentContainerStyle={{
                paddingLeft: LIST_PADDING_LEFT - 2, // -2 to account for a slight offset, source not 100% clear
              }}
              estimatedItemSize={estimatedItemSize}
              scrollEnabled={false}
            />
          )}
        </ListContainer>
      )}

      <StylistProfileModal
        id={selectedStylistId}
        isVisible={stylistProfileModalVisible}
        setIsVisible={setStylistProfileModalVisible}
        onConsultationButtonPress={onConsultationButtonPress}
        onDismiss={onStylistProfileModalDismiss}
      />
      <BookingModal
        isVisible={bookingModalVisible}
        setIsVisible={setBookingModalVisible}
      />
      <FiltersModal
        isVisible={isFilterModalVisible}
        setIsVisible={setIsFilterModalVisible}
      />
    </OuterContainer>
  );
};

export default Explore;

const OuterContainer = styled(KeyboardAwareScrollView)({
  flex: 1,
  backgroundColor: sand,
});

const ListContainer = styled.View({
  flex: 1,
  width: '100%',
});

const HeaderContainer = styled.View({
  padding: SCREEN_PADDING,
  paddingTop: 30,
  alignItems: 'center',
});

const StyledH3 = styled(H3)({
  maxWidth: 346,
  textAlign: 'center',
  textTransform: 'uppercase',
  marginBottom: 7,
  color: green,
});

const StyledBodyText = styled(BodyText)({
  maxWidth: 316,
  textAlign: 'center',
  marginBottom: 24,
  color: lightGreen,
});

const ButtonContainer = styled.View({
  marginBottom: 25,
});

const SearchContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

const searchStyles = css({
  height: 48,
  fontSize: 16,
  color: green,
  maxWidth: 274,
  width: '100%',
  paddingLeft: 0,
});

const IconImage = styled(Image)({
  resizeMode: 'contain',
  width: 20,
  height: 20,
});

const IconButton = styled(TouchableOpacity)({
  padding: 6,
});

const buttonTextStyle = css({
  fontWeight: 'regular',
  fontSize: 13,
  letterSpacing: -0.13,
});

const NoResultsContainer = styled.View({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  paddingVertical: 20,
  paddingHorizontal: 30,
});

const NoResultsTitle = styled(H3)({
  textAlign: 'center',
  color: green,
  marginBottom: 6,
  textTransform: 'uppercase',
  fontSize: 25,
});

const NoResultsText = styled(BodyText)({
  textAlign: 'center',
  color: green,
  fontSize: 16,
  maxWidth: 308,
  lineHeight: 20,
});

const ClearIcon = styled(Image)({
  width: 30,
  height: 30,
  resizeMode: 'contain',
});

const heartStyle = {
  width: 23,
  height: 23,
};

const activeHeartStyle = {
  height: 34,
  width: 34,
};
